import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  FileSvg, FilePng, Sparkle, Image as ImageIcon, Megaphone, Storefront, FlowArrow, ChartLine, Files, UsersFour,
} from '@phosphor-icons/react';
import TI from '../../components/brand/TI';
import { HaleWordmark } from '../../components/HaleBrand';

import MarketplaceOverviewAsset    from '../../components/hale-marketing/MarketplaceOverviewAsset';
import BrandJourneyAsset           from '../../components/hale-marketing/BrandJourneyAsset';
import GovernanceWorkflowAsset     from '../../components/hale-marketing/GovernanceWorkflowAsset';
import BrandDashboardMockupAsset   from '../../components/hale-marketing/BrandDashboardMockupAsset';
import ExecutiveOnePagerAsset      from '../../components/hale-marketing/ExecutiveOnePagerAsset';
import PilotAnnouncementAsset      from '../../components/hale-marketing/PilotAnnouncementAsset';
import LinkedInLaunchAsset         from '../../components/hale-marketing/LinkedInLaunchAsset';
import PartnerGraphicAsset         from '../../components/hale-marketing/PartnerGraphicAsset';
import PilotRecruitmentAsset       from '../../components/hale-marketing/PilotRecruitmentAsset';

import { downloadSvg, downloadPng, PRESETS } from '../../components/hale-marketing/exportUtils';

const IVORY = '#F5F1E8';
const SLATE = '#0B1426';
const CORAL = '#FF4E6A';
const MUTED = '#5B6577';
const RULE  = 'rgba(11,20,38,0.10)';

/* ====================================================================
 * /admin/hale/marketing — Hale Pilot Launch Assets
 *
 * Hosts the nine SVG-based marketing assets, each rendered live and
 * downloadable in SVG and multiple PNG sizes (presentation, website,
 * LinkedIn, square). Built on the same Hale × ToxIntel UI primitives
 * used elsewhere in the platform — no stock photography, no AI imagery.
 * ==================================================================== */

const PART_ONE = [
  { id: 'marketplace-overview',  title: 'Hale Marketplace Overview',  desc: 'Annotated marketplace screen with five governance category callouts.',
    icon: Storefront,  presets: ['presentation','website','linkedin'], Component: MarketplaceOverviewAsset, kind: 'Demo · presentation' },
  { id: 'brand-journey',         title: 'Hale Brand Journey',          desc: 'Vertical application → review → badge issuance → dashboard monitoring flow.',
    icon: FlowArrow,   presets: ['presentation','website','square'],   Component: BrandJourneyAsset,        kind: 'Demo · workflow' },
  { id: 'governance-workflow',   title: 'Hale Governance Workflow',    desc: 'Chain of accountability: Brand → Hale → ToxIntel → Scientific Assessment → Decision → Dashboard.',
    icon: ChartLine,   presets: ['presentation','website'],            Component: GovernanceWorkflowAsset,  kind: 'Demo · system diagram' },
  { id: 'brand-dashboard',       title: 'Brand Dashboard Mockup',      desc: 'Live-product mockup using actual dashboard components: status, reviewer, outstanding items, badge progress, next action.',
    icon: Files,       presets: ['presentation','website','linkedin'], Component: BrandDashboardMockupAsset, kind: 'Demo · product view' },
  { id: 'executive-one-pager',   title: 'Executive One-Pager',         desc: 'Single-page letter-portrait handout for investors, retailers, and pilot partners.',
    icon: FileSvg,     presets: ['presentation','square'],              Component: ExecutiveOnePagerAsset,    kind: 'Demo · one-pager' },
];

const PART_TWO = [
  { id: 'pilot-announcement',    title: 'Pilot Announcement Graphic',  desc: '"Hale Launches Independent Product Review Pilot" — wide hero card. Powered by ToxIntel · Scientific review by The Agyekum Group.',
    icon: Megaphone,    presets: ['presentation','website','linkedin'], Component: PilotAnnouncementAsset,   kind: 'Joint · announcement' },
  { id: 'linkedin-launch',       title: 'LinkedIn Launch Graphic',     desc: 'LinkedIn share size. Three-up brand lockup (Hale · ToxIntel · The Agyekum Group) with launch headline.',
    icon: ImageIcon,    presets: ['linkedin','website'],                Component: LinkedInLaunchAsset,       kind: 'Joint · social' },
  { id: 'partner-graphic',       title: 'Partner Graphic',             desc: '"Hale + The Agyekum Group" partnership square. Two-pathway support copy.',
    icon: UsersFour,   presets: ['square','linkedin'],                 Component: PartnerGraphicAsset,       kind: 'Joint · partnership' },
  { id: 'pilot-recruitment',     title: 'Pilot Brand Recruitment',     desc: '"Seeking Pilot Brands" call-to-action. Five governance highlights + apply CTA.',
    icon: Sparkle,      presets: ['square','linkedin','website'],       Component: PilotRecruitmentAsset,     kind: 'Joint · recruitment' },
];

const ALL_ASSETS = [...PART_ONE, ...PART_TWO];

function AssetCard({ asset }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(null); // preset key being exported
  const { Component, presets, title, desc, kind } = asset;

  const onDownloadSvg = () => {
    downloadSvg(ref.current, `${asset.id}.svg`);
  };
  const onDownloadPng = async (presetKey) => {
    setBusy(presetKey);
    try {
      const p = PRESETS[presetKey];
      await downloadPng(ref.current, `${asset.id}.${presetKey}.${p.w}x${p.h}.png`, p.w, p.h);
    } catch (e) {
      console.error('PNG export failed', e);
    } finally {
      setBusy(null);
    }
  };

  return (
    <section
      className="rounded-sm border p-6 mb-8"
      style={{ borderColor: RULE, backgroundColor: 'rgba(255,255,255,0.6)' }}
      data-testid={`asset-card-${asset.id}`}
    >
      <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <div>
          <p className="text-[10px] font-mono uppercase tracking-[0.3em] mb-1" style={{ color: CORAL }}>
            {kind}
          </p>
          <h3
            className="text-2xl font-extrabold tracking-tight"
            style={{ fontFamily: '"Cormorant Garamond", serif', color: SLATE }}
          >
            {title}
          </h3>
          <p className="mt-2 text-sm max-w-2xl" style={{ color: MUTED }}>{desc}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={onDownloadSvg}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-sm text-[11px] font-mono uppercase tracking-wider border"
            style={{ borderColor: SLATE, color: SLATE, backgroundColor: IVORY }}
            data-testid={`download-svg-${asset.id}`}
          >
            <FileSvg size={12} weight="bold" /> SVG
          </button>
          {presets.map(p => (
            <button
              key={p}
              type="button"
              onClick={() => onDownloadPng(p)}
              disabled={busy === p}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-sm text-[11px] font-mono uppercase tracking-wider transition-transform hover:-translate-y-px disabled:opacity-50"
              style={{ backgroundColor: CORAL, color: SLATE }}
              data-testid={`download-png-${asset.id}-${p}`}
            >
              <FilePng size={12} weight="bold" />
              {busy === p ? 'EXPORTING…' : `PNG · ${PRESETS[p].w}×${PRESETS[p].h}`}
            </button>
          ))}
        </div>
      </div>

      {/* Live preview */}
      <div
        className="rounded-sm overflow-hidden border"
        style={{ borderColor: RULE, backgroundColor: IVORY }}
        data-testid={`asset-preview-${asset.id}`}
      >
        <Component ref={ref} />
      </div>
    </section>
  );
}

export default function HaleMarketingAssetsPage() {
  return (
    <div
      className="min-h-screen"
      style={{ backgroundColor: IVORY, color: SLATE, fontFamily: 'Inter, system-ui, sans-serif' }}
      data-testid="hale-marketing-assets-page"
    >
      <Helmet>
        <title>Hale Marketing Assets · Pilot Launch · ToxIntel</title>
      </Helmet>

      <header className="border-b" style={{ borderColor: RULE }}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" className="flex items-center gap-3" aria-label="ToxIntel home">
              <span className="text-xl tracking-tight" style={{ color: SLATE, fontFamily: '"Cormorant Garamond", serif' }}>
                <TI color={CORAL} />
              </span>
            </Link>
            <span
              className="ml-3 text-[10px] font-mono uppercase tracking-[0.25em] px-2 py-1 rounded-sm"
              style={{ backgroundColor: SLATE, color: IVORY }}
            >
              · Admin · Hale · Marketing
            </span>
          </div>
          <nav className="flex items-center gap-5">
            <Link to="/admin/hale" className="text-[11px] uppercase tracking-[0.18em]" style={{ color: MUTED }} data-testid="marketing-nav-back">
              ← Hale admin hub
            </Link>
            <Link to="/hale" className="text-[11px] uppercase tracking-[0.18em]" style={{ color: MUTED }} data-testid="marketing-nav-public">
              Public /hale →
            </Link>
          </nav>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-12 lg:py-16">
        {/* Hero */}
        <div className="mb-10">
          <HaleWordmark size="md" />
          <p className="mt-5 text-[10px] font-mono uppercase tracking-[0.3em]" style={{ color: CORAL }}>
            Pilot launch assets · marketing
          </p>
          <h1
            className="mt-3 text-3xl sm:text-4xl font-extrabold tracking-tight"
            style={{ fontFamily: '"Cormorant Garamond", serif', color: SLATE }}
            data-testid="marketing-title"
          >
            Hale pilot launch · marketing assets.
          </h1>
          <p className="mt-3 text-sm max-w-3xl" style={{ color: MUTED }}>
            Presentation-ready visuals built from the live Hale × ToxIntel UI system —
            no stock photography, no AI imagery. Each asset is downloadable as scalable
            SVG and as PNG in presentation, website, LinkedIn, and square dimensions.
            Brand hierarchy is locked: <span className="font-mono">HALE</span> · Powered by{' '}
            <span className="font-mono">TOXINTEL</span> · Scientific review partner: <span className="font-mono">THE AGYEKUM GROUP</span>.
          </p>
        </div>

        {/* Inventory summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10" data-testid="marketing-inventory-summary">
          {[
            { k: 'Demo assets',        v: PART_ONE.length },
            { k: 'Joint marketing',    v: PART_TWO.length },
            { k: 'Export formats',     v: 'SVG · PNG' },
            { k: 'Preset sizes',       v: Object.keys(PRESETS).length },
          ].map(s => (
            <div key={s.k} className="rounded-sm border p-5" style={{ borderColor: RULE, backgroundColor: 'rgba(255,255,255,0.6)' }}>
              <p className="text-[10px] font-mono uppercase tracking-[0.25em] mb-2" style={{ color: MUTED }}>{s.k}</p>
              <p className="text-3xl font-extrabold tabular-nums" style={{ fontFamily: '"Cormorant Garamond", serif', color: SLATE }}>
                {s.v}
              </p>
            </div>
          ))}
        </div>

        {/* PART 1 — Demo visuals */}
        <div className="mb-6 mt-4">
          <p className="text-[10px] font-mono uppercase tracking-[0.3em]" style={{ color: CORAL }}>Part 1 · Hale demo visual library</p>
          <h2
            className="text-2xl font-extrabold tracking-tight mt-1"
            style={{ fontFamily: '"Cormorant Garamond", serif', color: SLATE }}
          >
            Demo &amp; presentation visuals — built from the live product.
          </h2>
        </div>
        {PART_ONE.map(a => <AssetCard key={a.id} asset={a} />)}

        {/* PART 2 — Joint marketing */}
        <div className="mb-6 mt-12">
          <p className="text-[10px] font-mono uppercase tracking-[0.3em]" style={{ color: CORAL }}>Part 2 · Joint Hale + Agyekum pilot marketing</p>
          <h2
            className="text-2xl font-extrabold tracking-tight mt-1"
            style={{ fontFamily: '"Cormorant Garamond", serif', color: SLATE }}
          >
            Co-branded pilot launch assets.
          </h2>
          <p className="mt-2 text-sm max-w-3xl" style={{ color: MUTED }}>
            Brand hierarchy is locked: Hale is the program, ToxIntel is the platform,
            The Agyekum Group is the scientific review partner. The Agyekum Group is
            never positioned above Hale on any Hale-facing asset.
          </p>
        </div>
        {PART_TWO.map(a => <AssetCard key={a.id} asset={a} />)}

        {/* Inventory footer */}
        <section
          className="rounded-sm border p-7 mt-10"
          style={{ borderColor: RULE, backgroundColor: 'rgba(11,20,38,0.04)' }}
          data-testid="marketing-inventory-footer"
        >
          <p className="text-[10px] font-mono uppercase tracking-[0.3em] mb-2" style={{ color: CORAL }}>
            Asset inventory · pilot launch 2026
          </p>
          <h3 className="text-xl font-extrabold tracking-tight mb-4"
              style={{ fontFamily: '"Cormorant Garamond", serif', color: SLATE }}>
            Recommended assets for demo presentation
          </h3>
          <ul className="text-sm space-y-2 max-w-3xl" style={{ color: SLATE }}>
            <li>· <span className="font-semibold">Slide 1 — opener:</span> Hale Marketplace Overview (presentation 16:9)</li>
            <li>· <span className="font-semibold">Slide 2 — workflow:</span> Hale Brand Journey (vertical) + Governance Workflow (lateral)</li>
            <li>· <span className="font-semibold">Slide 3 — product proof:</span> Brand Dashboard Mockup (presentation 16:9)</li>
            <li>· <span className="font-semibold">Slide 4 — partnership:</span> Partner Graphic (square)</li>
            <li>· <span className="font-semibold">Leave-behind:</span> Executive One-Pager (PDF-ready square / portrait)</li>
            <li>· <span className="font-semibold">Public launch:</span> Pilot Announcement (LinkedIn 1200×627) + Pilot Recruitment (LinkedIn square)</li>
          </ul>
          <p className="mt-5 text-[12px]" style={{ color: MUTED }}>
            All assets reuse the live Hale × ToxIntel design system — IVORY/CORAL/SLATE palette,
            Cormorant Garamond + Inter typography, HALE block wordmark, and the <TI color={CORAL} /> serif lockup.
            Source SVG components live at <span className="font-mono">/app/frontend/src/components/hale-marketing/</span>.
          </p>
        </section>
      </main>
    </div>
  );
}
